import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Switch, useLocation, Redirect, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CryptoJS from "crypto-js";
import { SSO_LOGOUT_URL } from "../api/url";
import { LogonRoute } from "./loginRoutes";
import { CmsRoute } from "./cmsRoutes";
import ProtectedRoute, { IsUserLoggedIn } from "./protectedRoutes";
import LandingPage from "../pages/landingPage";
import Login from "../pages/loginPage";
import CmsLogin from "../pages/loginPage/cmsLogin";
import RequestEventDetailPage from "../pages/requestEventDetailPage";
import QuickLinksDetailsPage from "../pages/quickLinkDetailsPage";
import PhotoApprovalPage from "../pages/photoApprovalPage";
import NotificationPage from "../pages/notification";
import ReviewApprovalPage from "../pages/reviewApprovalPage";
import PhotoDetailPage from "../pages/photoDetailPage";
import PackagesDetailsPage from "../pages/packagesListingPage/modalChildren";
import SurveyFormPage from "../pages/surveyFormPage";
import SurveyFormResponses from "../pages/SurveyFormResponses.tsx";
import EventInsightsPage from "../pages/EventInsightsPage.tsx";
import QuotationsPage from "../pages/quotationsPage";
import { getUserInformations, userLogoutAPI } from "../api/apis/login";
import { checkEventCoordinatorAPI } from "../api/apis/workflow";
import { Toaster } from "../components/toaster";
import { isLoggedIn, setCoordinatorStatus } from "../store/reducers/userSlice";
import config from "../utils/config";
import IdleTimer from "../components/idleTimer";
import App from "../App.tsx";

const AppRoutes = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const toaster = Toaster();
  const isNavigating = useRef(false);
  const [rendered, setRendered] = useState(false);
  const isDownloading = useSelector((state) => state.event.isDownloading);

  const handleLogout = useCallback(async () => {
    let res = await userLogoutAPI();
    if (res.error) {
      if (res && res.code && res.code === 401) {
        <Redirect to="/" />;
        dispatch(isLoggedIn(false));
        sessionStorage.clear();
        toaster("Unauthorized", "error", "error");
        return;
      }
      toaster(res.message, "error", "error");
      return;
    } else {
      <Redirect to="/" />;
      dispatch(isLoggedIn(false));
      sessionStorage.clear();
      toaster("You have been logged out due to inactivity", "error", "error");
      return;
    }
  }, [history]);

  useEffect(() => {
    if (rendered) {
      const unlisten = history.listen(() => {
        isNavigating.current = true;
      });

      const handleBeforeUnload = (event) => {
        if (!isNavigating.current && !isDownloading) {
          // This block only runs if the tab or window is closed
          event.preventDefault();
          event.returnValue = "";
          handleLogout();
        }
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        unlisten();
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
    setRendered(true);
  }, [history]);

  useEffect(() => {
    if (location.pathname === "/redirect") {
      const tokenFromUrl = getTokenFromUrl(location.search);
      const decodedString = decodeURIComponent(tokenFromUrl);
      const jsonString = decodedString.replace(/'/g, '"');
      if (jsonString.includes("Too")) toaster(jsonString, "error", "error");
      else {
        try {
          const jsonObject = JSON.parse(jsonString);
          sessionStorage.setItem(
            "access_token",
            CryptoJS.AES.encrypt(
              JSON.stringify(jsonObject.access_token),
              config.tokenKey
            ).toString()
          );
          sessionStorage.setItem(
            "refresh_token",
            CryptoJS.AES.encrypt(
              JSON.stringify(jsonObject.refresh_token),
              config.tokenKey
            ).toString()
          );
          sessionStorage.setItem(
            "session_id",
            CryptoJS.AES.encrypt(
              JSON.stringify(jsonObject.session_id),
              config.tokenKey
            ).toString()
          );
        } catch (e) {
          sessionStorage.setItem(
            "access_token",
            CryptoJS.AES.encrypt(
              JSON.stringify(tokenFromUrl),
              config.tokenKey
            ).toString()
          );
        }
        // Check if this is CMS login
        const isCmsLogin = sessionStorage.getItem("cmsLogin");
        if (isCmsLogin === "true") {
          // Don't clear the flag yet - keep it for back navigation
          getUserInformationForCms();
        } else {
          getUserInformation();
        }
      }

      if (sessionStorage.getItem("access_token")) {
        const bytes = CryptoJS.AES.decrypt(
          sessionStorage.getItem("access_token"),
          config.tokenKey
        );
        const jsonString = bytes.toString(CryptoJS.enc.Utf8);
        const token = JSON.parse(jsonString);
        if (token && token !== null) getUserInformation();
      }
    }
  }, []);

  // Check coordinator status on app initialization
  useEffect(() => {
    const checkCoordinatorOnInit = async () => {
      if (sessionStorage.getItem("access_token")) {
        try {
          // Get user information to check role
          const info = await getUserInformations();
          if (info?.error) {
            dispatch(setCoordinatorStatus(false));
            return;
          }
          
          // Check user role from getUserInformations response
          const userRole = info?.data?.data?.data?.role;
          if (userRole === 'vendor' || userRole === 'External_employee' || userRole === 'employee') {
            // Skip API call for vendor, external employee, and employee roles
            dispatch(setCoordinatorStatus(false));
          } else {
            // Call API for other roles
            const coordinatorRes = await checkEventCoordinatorAPI();
            if (coordinatorRes?.error) {
              dispatch(setCoordinatorStatus(false));
            } else {
              const isCoordinator = coordinatorRes.data?.data?.status_code === 200;
              dispatch(setCoordinatorStatus(isCoordinator));
            }
          }
        } catch (error) {
          dispatch(setCoordinatorStatus(false));
        }
      }
    };

    checkCoordinatorOnInit();
  }, []);

  function getTokenFromUrl(search) {
    const params = new URLSearchParams(search);
    return params.get("TN");
  }

  async function getUserInformationForCms() {
    const info = await getUserInformations();
    if (info.code === 401) {
      const microsoftLoginUrl = config.authUrl + SSO_LOGOUT_URL;
      window.location.href = microsoftLoginUrl;
      dispatch(isLoggedIn(false));
      sessionStorage.clear();
      return <Redirect to="/" />;
    }
    if (info.error) {
      Toaster(info.message, "error", "error");
      return <Redirect path="/" />;
    } else {
      if (sessionStorage.getItem("access_token")) {
        const bytes = CryptoJS.AES.decrypt(
          sessionStorage.getItem("access_token"),
          config.tokenKey
        );
        const jsonString = bytes.toString(CryptoJS.enc.Utf8);
        const token = JSON.parse(jsonString);
        if (token && token !== null) {
          sessionStorage.setItem(
            "content",
            CryptoJS.AES.encrypt(
              JSON.stringify(info?.data?.data?.data),
              config.userKey
            ).toString()
          );
          // Don't dispatch isLoggedIn(true) for CMS login
          // Redirect directly to CMS portal
          const { generateCMSRedirectURL } = await import("../utils/cmsRedirect");
          const cmsURL = generateCMSRedirectURL();
          if (cmsURL) {
            // Clear the flag only when successfully redirecting to CMS
            sessionStorage.removeItem("cmsLogin");
            window.location.href = cmsURL;
          }
        }
      }
    }
  }

  async function getUserInformation() {
    const info = await getUserInformations();
    if (info.code === 401) {
      const microsoftLoginUrl = config.authUrl + SSO_LOGOUT_URL;
      window.location.href = microsoftLoginUrl;
      dispatch(isLoggedIn(false));
      sessionStorage.clear();
      return <Redirect to="/" />;
    }
    if (info.error) {
      Toaster(info.message, "error", "error");
      return <Redirect path="/" />;
    } else {
      if (sessionStorage.getItem("access_token")) {
        const bytes = CryptoJS.AES.decrypt(
          sessionStorage.getItem("access_token"),
          config.tokenKey
        );
        const jsonString = bytes.toString(CryptoJS.enc.Utf8);
        const token = JSON.parse(jsonString);
        if (token && token !== null) {
          sessionStorage.setItem(
            "content",
            CryptoJS.AES.encrypt(
              JSON.stringify(info?.data?.data?.data),
              config.userKey
            ).toString()
          );
          dispatch(isLoggedIn(true));
          
          // Check coordinator status
          try {
            // Check user role from getUserInformations response
            const userRole = info?.data?.data?.data?.role;
            if (userRole === 'vendor' || userRole === 'External_employee' || userRole === 'employee') {
              // Skip API call for vendor, external employee, and employee roles
              dispatch(setCoordinatorStatus(false));
            } else {
              // Call API for other roles
              const coordinatorRes = await checkEventCoordinatorAPI();
              if (coordinatorRes?.error) {
                dispatch(setCoordinatorStatus(false));
              } else {
                // If status_code is 200, user is coordinator
                const isCoordinator = coordinatorRes.data?.data?.status_code === 200;
                dispatch(setCoordinatorStatus(isCoordinator));
              }
            }
          } catch (error) {
            dispatch(setCoordinatorStatus(false));
          }
        }
      }
    }
  }

  return (
    <Suspense fallback={<p>Loading....</p>}>
      {window.location.pathname !== "/" && <IdleTimer onIdle={handleLogout} />}
      <Switch>
        <LogonRoute exact path="/" component={Login} />
        <CmsRoute exact path="/CMS" component={CmsLogin} />
        <CmsRoute exact path="/CMS/redirect" component={CmsLogin} />
        <ProtectedRoute
          exact
          path={"/home"}
          component={LandingPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/redirect"}
          component={LandingPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/event-detail"}
          component={RequestEventDetailPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/event-detail/quotations"}
          component={QuotationsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/cost-benchmarking"}
          component={EventInsightsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/policy"}
          link_type={"POLICIES_DOCUMENTS"}
          component={QuickLinksDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/travel-partners"}
          link_type={"TRAVEL_PARTNER"}
          component={QuickLinksDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/images"}
          link_type={"IMAGES"}
          component={QuickLinksDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/reviews"}
          link_type={"REVIEWS"}
          component={QuickLinksDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/wallets"}
          link_type={"WALLET"}
          component={QuickLinksDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/dashboard"}
          link_type={"DASHBOARD"}
          component={QuickLinksDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/event-detail/photo-detail"}
          link_type={"PHOTO_APPROVAL"}
          component={PhotoApprovalPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/event-detail/all-photo-detail"}
          link_type={"PHOTO_APPROVAL"}
          component={PhotoDetailPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/event-detail/all-review-detail"}
          link_type={"REVIEW_APPROVAL"}
          component={ReviewApprovalPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/notification"}
          link_type={"NOTIFICATION"}
          component={NotificationPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/packages-detail"}
          link_type={"PACKAGE_LISTING_DETAIL"}
          component={PackagesDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/package-detail"}
          link_type={"PACKAGE_DETAIL"}
          component={PackagesDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/cost-benchmark"}
          component={App}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/users"}
          link_type={"USERS_LISTING"}
          component={QuickLinksDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/user-access-requests"}
          link_type={"USER_ACCESS_REQUESTS"}
          component={QuickLinksDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/quotation-requests"}
          link_type={"QUOTATION_REQUESTS"}
          component={QuickLinksDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/survey-forms"}
          link_type={"SURVEY_FORM"}
          component={QuickLinksDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/archive-folder"}
          link_type={"ARCHIVE_FOLDER"}
          component={QuickLinksDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/benchmark-data"}
          link_type={"BENCHMARK_DATA"}
          component={QuickLinksDetailsPage}
          access={IsUserLoggedIn()}
        />
        <ProtectedRoute
          exact
          path={"/survey-form-responses"}
          component={SurveyFormResponses}
          access={IsUserLoggedIn()}
        />
        <LogonRoute
          exact
          path={"/survey-form"}
          component={SurveyFormPage}
        />
      </Switch>
    </Suspense>
  );
};

export default AppRoutes;
